import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTrigger,
  Flex,
  MetadataItem,
  Text,
  TextField,
  useToast,
} from '@chatsdet/ui';
import { useAuth } from '../../../contexts/AuthContext';
import { BRAND } from '../../../brand';
import {
  useOrgAppSubdomain,
  useSetOrgAppSubdomain,
} from '../../../hooks/useOrgAppSubdomain';
import { CopyValueButton } from './CopyValueButton';

type WorkspaceAddressRowProps = {
  isSuperadmin: boolean;
};

function formatClaimedAt(claimedAt: string) {
  return new Date(claimedAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Must be rendered as a direct child of an org-detail `divide-y` list (see
 * Settings.tsx) — it returns a single `MetadataItem` row and nothing else,
 * so it is not a self-contained section and would produce an unstyled
 * orphan row anywhere else.
 */
export function WorkspaceAddressRow({ isSuperadmin }: WorkspaceAddressRowProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const brandName = BRAND.name;
  const subdomainQuery = useOrgAppSubdomain();
  const setMutation = useSetOrgAppSubdomain(user?.orgId);

  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [isClearOpen, setIsClearOpen] = React.useState(false);
  const [newLabel, setNewLabel] = React.useState('');

  const data = subdomainQuery.data?.data;
  const retired = data?.retired ?? [];

  const openEdit = () => {
    setNewLabel(data?.current ?? '');
    setIsEditOpen(true);
  };

  const handleSave = () => {
    setMutation.mutate(newLabel.trim().toLowerCase(), {
      onSuccess: () => {
        toast.success('Workspace address updated');
        setIsEditOpen(false);
        setNewLabel('');
      },
      onError: (error: Error) => {
        toast.error(error.message);
      },
    });
  };

  const handleClear = () => {
    setMutation.mutate(null, {
      onSuccess: () => {
        toast.success(`Moved back to the shared ${brandName} address`);
        setIsClearOpen(false);
      },
      onError: (error: Error) => {
        toast.error(error.message);
        setIsClearOpen(false);
      },
    });
  };

  return (
    <MetadataItem
      label="Workspace address"
      action={
        isSuperadmin && data ? (
          <Flex align="center" gap="sm">
            {data.current && (
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive"
                onPress={() => setIsClearOpen(true)}
              >
                Return to shared address
              </Button>
            )}
            <Button variant="outline" size="sm" onPress={openEdit}>
              {data.current ? 'Change' : 'Set custom address'}
            </Button>
          </Flex>
        ) : undefined
      }
    >
      {subdomainQuery.isLoading ? (
        <Text size="sm" className="text-muted-foreground">
          Loading…
        </Text>
      ) : data ? (
        <div className="space-y-1.5">
          <Flex align="center" gap="xs" className="min-w-0">
            {/* A value, not a page title. At heading scale it dwarfed the org
                name beside it, because a full URL is several times longer
                than the org name that heading size was chosen for. */}
            <Text size="sm" className="font-mono truncate" title={data.appUrl}>
              {data.appUrl}
            </Text>
            <CopyValueButton value={data.appUrl} label="Workspace address" />
          </Flex>

          {!isSuperadmin && (
            <Text size="sm" className="text-muted-foreground">
              The workspace address is managed by {brandName} support.
            </Text>
          )}

          {retired.length > 0 && (
            <div className="space-y-1.5">
              {/*
                "Still redirect" holds only while there is somewhere to redirect
                TO. An org that retired a label and then returned to the shared
                address holds retired labels with no current one, and those hosts
                answer "workspace not found". Both states are reachable from this
                screen, so neither sentence can be the unconditional one.
              */}
              <Text size="sm" className="text-muted-foreground">
                {data.current
                  ? 'Old addresses still redirect here and stay reserved to this organization:'
                  : "These addresses no longer resolve — there's no current address to redirect to — but stay reserved to this organization:"}
              </Text>
              <Flex wrap gap="xs">
                {retired.map((r) => (
                  <code
                    key={r.label}
                    className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono text-muted-foreground"
                  >
                    {r.label}
                    {/* The claim date is inline, not a `title` tooltip — a
                        `title` is mouse-only and unreachable on touch/keyboard. */}
                    <span className="text-muted-foreground/70">
                      {' · '}
                      {formatClaimedAt(r.claimedAt)}
                    </span>
                  </code>
                ))}
              </Flex>
            </div>
          )}
        </div>
      ) : (
        <Text size="sm" className="text-muted-foreground">
          Unable to load the workspace address.
        </Text>
      )}

      {/*
        Both dialog anchors (below and the clear-confirm dialog further down)
        live here, inside the MetadataItem's value column, rather than as
        siblings of it: DialogTrigger renders its first child (a bare
        `<span />`) inline, and as a sibling of this row that span would
        become its own `divide-y` item and draw a stray border.

        Both also use `size="xl"` (was `md`): sized for the explanatory
        paragraph that replaced the old warning Banner, which was cramped
        at `md`.
      */}
      {/* Set/change dialog */}
      <DialogTrigger isOpen={isEditOpen} onOpenChange={setIsEditOpen} size="xl">
        <span />
        <Dialog
          title={data?.current ? 'Change Workspace Address' : 'Set Workspace Address'}
          description="Pick a short label. It becomes the org's dedicated login and app URL."
          footer={
            <>
              <Button variant="outline" onPress={() => setIsEditOpen(false)} type="button">
                Cancel
              </Button>
              <Button
                onPress={handleSave}
                isDisabled={
                  !newLabel.trim() ||
                  newLabel.trim().toLowerCase() === data?.current ||
                  setMutation.isPending
                }
              >
                {setMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </>
          }
        >
          <Box className="space-y-4">
            <TextField
              label="Label"
              value={newLabel}
              onChange={setNewLabel}
              placeholder="acme"
              description="Lowercase letters, numbers, and hyphens only."
              autoFocus
            />
            <Text size="sm" className="text-muted-foreground">
              {data?.current
                ? // Cookies are __Host- scoped to the exact hostname, so a
                  // rename doesn't carry the session — members must sign in
                  // again on the new address. The old one keeps redirecting.
                  'This is the address members sign in on, and the address in the links we email and post to Slack. Members signed in on the current address will need to sign in again on the new one, and the old address will keep redirecting here.'
                : 'This becomes the address members sign in on, and the address in the links we email and post to Slack. Members will need to sign in again there. Once claimed, the name is reserved to this organization permanently and can never be transferred.'}
            </Text>
          </Box>
        </Dialog>
      </DialogTrigger>

      {/* Clear confirm dialog */}
      <DialogTrigger isOpen={isClearOpen} onOpenChange={setIsClearOpen} size="xl">
        <span />
        <Dialog
          title="Return to Shared Address"
          description={
            data?.current
              ? `Move this organization off ${data.appUrl} and back to the shared ${brandName} address?`
              : ''
          }
          footer={
            <>
              <Button variant="outline" onPress={() => setIsClearOpen(false)} type="button">
                Cancel
              </Button>
              <Button
                variant="destructive"
                onPress={handleClear}
                isDisabled={setMutation.isPending}
              >
                {setMutation.isPending ? 'Moving…' : 'Return to shared address'}
              </Button>
            </>
          }
        >
          {/*
            A rename leaves the old label pointing at the new one. Clearing
            does NOT: with no current label there is nowhere to redirect, so
            the server treats that host as belonging to no organization and it
            renders "workspace not found" (`resolveHostPinnedOrg` in
            `apps/api/src/middleware/organization-context.ts` answers a
            retired label with no current one as `ORGANIZATION_NOT_FOUND`,
            exactly as it answers a label nobody ever claimed).
            Promising a redirect here would be a promise the system does not
            make, on the one action a user cannot undo by retyping the label.
          */}
          <Text size="sm" className="text-muted-foreground">
            Members signed in on {data?.current ?? 'the current address'} will need to
            sign in again on the shared address. Links to the old address will stop
            working — unlike a rename, there's no new address to send them to. The
            name stays reserved to this organization and can be set again later.
          </Text>
        </Dialog>
      </DialogTrigger>
    </MetadataItem>
  );
}
